import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Heart, MessageCircle, ChevronLeft, ChevronRight, Trash2, Send, Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANK_COLORS = {
  pro: "#4A90D9", elite: "#9B59B6", master: "#F1C40F",
  grandmaster: "#E74C3C", legendary: "#E07A5F"
};

export function PostCard({ post, onLikeToggle, onDelete, onCommentAdded, showProviderInfo = true }) {
  const { user, token, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);
  const [isLiked, setIsLiked] = useState(post.is_liked || false);
  const [likeLoading, setLikeLoading] = useState(false);

  const images = post.images || [];
  const hasImages = images.length > 0;
  const authHeader = { 'Authorization': `Bearer ${token || localStorage.getItem('token')}` };

  const handleLike = async () => {
    if (!isAuthenticated) { toast.error(t('blog.loginToLike')); return; }
    setLikeLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/posts/${post.post_id}/like`, {
        method: 'POST', headers: authHeader
      });
      if (res.ok) {
        const data = await res.json();
        setIsLiked(data.liked);
        setLikesCount(data.likes_count);
        onLikeToggle?.(post.post_id, data.liked, data.likes_count);
      }
    } catch (e) { toast.error('Erreur'); }
    finally { setLikeLoading(false); }
  };

  const loadComments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/posts/${post.post_id}/comments`);
      if (res.ok) { setComments(await res.json()); setCommentsLoaded(true); }
    } catch (e) { console.error(e); }
  };

  const toggleComments = () => {
    if (!showComments && !commentsLoaded) loadComments();
    setShowComments(!showComments);
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    if (!isAuthenticated) { toast.error(t('blog.loginToComment')); return; }
    setCommentLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/posts/${post.post_id}/comments`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newComment })
      });
      if (res.ok) {
        const comment = await res.json();
        setComments(prev => [...prev, comment]);
        setNewComment('');
        onCommentAdded?.(post.post_id);
      }
    } catch (e) { toast.error('Erreur'); }
    finally { setCommentLoading(false); }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const res = await fetch(`${API_URL}/api/posts/comments/${commentId}`, {
        method: 'DELETE', headers: authHeader
      });
      if (res.ok) setComments(prev => prev.filter(c => c.comment_id !== commentId));
    } catch (e) { toast.error('Erreur'); }
  };

  const getImageUrl = (path) => {
    const tok = token || localStorage.getItem('token');
    return `${API_URL}/api/files/${path}?auth=${tok}`;
  };

  const timeAgo = (dateStr) => {
    const d = new Date(dateStr);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return t('blog.justNow');
    if (diff < 3600) return `${Math.floor(diff / 60)} ${t('blog.minutesAgo')}`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ${t('blog.hoursAgo')}`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} ${t('blog.daysAgo')}`;
    return d.toLocaleDateString();
  };

  const isOwner = user?.user_id && post.provider_name === user?.name;

  return (
    <div className="bg-card border border-border/50 rounded-2xl overflow-hidden shadow-soft" data-testid={`post-${post.post_id}`}>
      {/* Header */}
      {showProviderInfo && (
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10">
              {post.provider_image ? <AvatarImage src={post.provider_image} /> : null}
              <AvatarFallback className="bg-accent text-accent-foreground">{post.provider_name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-sm">{post.provider_name}</p>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs py-0 px-1.5" style={{ borderColor: RANK_COLORS[post.provider_rank], color: RANK_COLORS[post.provider_rank] }}>
                  {post.provider_rank}
                </Badge>
                <span className="text-xs text-muted-foreground">{timeAgo(post.created_at)}</span>
              </div>
            </div>
          </div>
          {isOwner && onDelete && (
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={() => onDelete(post.post_id)} data-testid={`delete-post-${post.post_id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}

      {/* Image Carousel */}
      {hasImages && (
        <div className="relative aspect-square bg-muted">
          <img src={getImageUrl(images[currentImageIndex])} alt="" className="w-full h-full object-cover" loading="lazy" />
          {images.length > 1 && (
            <>
              {currentImageIndex > 0 && (
                <button onClick={() => setCurrentImageIndex(i => i - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors">
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              {currentImageIndex < images.length - 1 && (
                <button onClick={() => setCurrentImageIndex(i => i + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors">
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {images.map((_, i) => (
                  <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === currentImageIndex ? 'bg-white' : 'bg-white/50'}`} />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Actions + Caption */}
      <div className="p-4">
        <div className="flex items-center gap-4 mb-3">
          <button onClick={handleLike} disabled={likeLoading} className="flex items-center gap-1.5 group" data-testid={`like-btn-${post.post_id}`}>
            <Heart className={`w-6 h-6 transition-colors ${isLiked ? 'fill-red-500 text-red-500' : 'text-foreground group-hover:text-red-500'}`} />
            <span className="text-sm font-medium">{likesCount}</span>
          </button>
          <button onClick={toggleComments} className="flex items-center gap-1.5 group" data-testid={`comments-btn-${post.post_id}`}>
            <MessageCircle className="w-6 h-6 text-foreground group-hover:text-accent transition-colors" />
            <span className="text-sm font-medium">{post.comments_count || comments.length}</span>
          </button>
        </div>

        {/* Caption */}
        <p className="text-sm">
          {showProviderInfo && <span className="font-semibold mr-1">{post.provider_name}</span>}
          {post.caption}
        </p>

        {/* Comments Section */}
        {showComments && (
          <div className="mt-4 pt-4 border-t border-border/50">
            {comments.length === 0 && commentsLoaded && (
              <p className="text-sm text-muted-foreground mb-3">{t('blog.noComments')}</p>
            )}
            <div className="space-y-3 max-h-60 overflow-y-auto mb-3">
              {comments.map(c => (
                <div key={c.comment_id} className="flex gap-2 group" data-testid={`comment-${c.comment_id}`}>
                  <Avatar className="w-7 h-7 flex-shrink-0">
                    {c.user_picture ? <AvatarImage src={c.user_picture} /> : null}
                    <AvatarFallback className="text-xs">{c.user_name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm"><span className="font-semibold mr-1">{c.user_name}</span>{c.content}</p>
                    <span className="text-xs text-muted-foreground">{timeAgo(c.created_at)}</span>
                  </div>
                  {(c.user_id === user?.user_id || isOwner) && (
                    <button onClick={() => handleDeleteComment(c.comment_id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {isAuthenticated && (
              <form onSubmit={handleComment} className="flex gap-2">
                <Input placeholder={t('blog.addComment')} value={newComment} onChange={e => setNewComment(e.target.value)} className="h-9 text-sm" data-testid={`comment-input-${post.post_id}`} />
                <Button type="submit" size="sm" disabled={commentLoading || !newComment.trim()} className="h-9 px-3" data-testid={`send-comment-${post.post_id}`}>
                  {commentLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
